<script lang="ts" setup>
import { useModal } from '@monogrid/vue-boost/integrations/focus-trap'
import { UseFocusTrapOptions } from '@vueuse/integrations/useFocusTrap.mjs'
import { PropType, ref } from 'vue'

import AppButtonIcon from '../atoms/AppButtonIcon.vue'

defineOptions({ inheritAttrs: false })

const visible = defineModel<boolean>('visible')
const emit = defineEmits(['close'])
const props = defineProps({
  focusTrapOptions: {
    type: Object as PropType<UseFocusTrapOptions>,
    default: () => ({
      initialFocus: false,
      allowOutsideClick: true,
      returnFocusOnDeactivate: false
    } as UseFocusTrapOptions)
  },
  fullscreen: { type: Boolean, default: false },
  small: { type: Boolean, default: true }
})

const contentEl = ref<HTMLElement>(null)
const wrapper = ref<HTMLElement>(null)

const close = () => {
  if (!visible.value) return
  visible.value = false
  emit('close')
}

useModal(wrapper, close, { ...props.focusTrapOptions })

defineExpose({ wrapper })
</script>

<template>
  <Teleport to="body">
    <Transition>
      <div
        v-if="visible"
        v-bind="$attrs"
        ref="wrapper"
        aria-modal="true"
        class="modal fixed inset-0 z-1000 flex items-center justify-center"
        role="dialog"
      >
        <div class="absolute inset-0 cursor-pointer" @click.self="close" />
        <div
          :class="{
            'lg:border lg:border-black lg:max-h-[calc(100%-200px)] lg:mx-8': !fullscreen,
          }"
          class="wrapper relative w-full h-full overflow-hidden bg-white"
        >
          <header class="absolute top-4 right-4 lg:top-8 lg:right-8 z-10">
            <slot name="close" :close="close">
              <AppButtonIcon
                icon="cross"
                @click="close"
              />
            </slot>
          </header>
          <main ref="contentEl" class="h-full overflow-x-hidden overflow-y-auto">
            <slot />
          </main>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.wrapper::before {
  position: absolute;
  z-index: 1;
  top: 0;
  right: rem(16px);
  left: 0;
  height: rem(125px);
  background: linear-gradient(rgba(color('white'), 1) 50%, rgba(color('white'), 0) 100%);
  content: "";
  pointer-events: none;

  @screen lg {
    height: rem(100px);
    background: linear-gradient(rgba(color('white'), 1) 0%, rgba(color('white'), 0) 100%);
  }
}
</style>
